import {getTestProps} from '@benchling/js-test-utils';
import {clamp} from '@benchling/js-utils';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import findScrollContainer from 'util/findScrollContainer';
import {ElementMouseEvent} from 'util/Types';

import ReorderableListItem from './ReorderableListItem';

const AUTOSCROLL_DISTANCE = 20;
const AUTOSCROLL_TRIGGER_DISTANCE = 50;
const AUTOSCROLL_TRIGGER_RATE = 32;

export interface ItemData<T> {
  id: string;
  itemProps: T;
  itemClass: React.ComponentType<T>;
}

export interface ReorderableListProps<T> {
  itemDatas: Array<ItemData<T>>;
  onMoveItem: (ids: Array<string>) => void;
  onMoveItemEnd: (id: string) => void;
}

interface State {
  dragParams: {
    itemId: string;
    itemHeight: number;
    itemWidth: number;
    computeTranslateFromMouseY: (pageY: number) => number;
    itemTranslateYs: Array<number>;
  } | null;
  dragState: {translateY: number; dragIndex: number} | null;
}

function getItemRef(index: number): string {
  return `item${index}`;
}

function getOffsetTop(node: Element | null): number {
  assert(node != null, 'Expected node to be non-null');
  return node.getBoundingClientRect().top;
}

/**
 * Creates a function that calculates the y-translation of a dragged item from the screen coordinates of the
 * mouse.
 *
 * Note that we do not cache the list container's position because it changes if the page or a scroll
 * container scrolls while dragging. The y-translation constants (min, max, etc.) are cached because they
 * don't change.
 *
 * @param dragStartDeltaY Distance in pixels between the top of the page and the y-coordinate where the
 *                        dragged item was initially clicked
 * @param listContainerNode Rendered list container element
 * @param maxTranslateY Maximum distance in pixels that the dragged item can be dragged down the page
 * @package
 */
export function makeComputeTranslateFromMouseY({
  dragStartDeltaY,
  listContainerNode,
  maxTranslateY,
}: {
  dragStartDeltaY: number;
  listContainerNode: HTMLElement;
  maxTranslateY: number;
}): (pageY: number) => number {
  return (pageY: number) =>
    clamp(0, maxTranslateY, pageY - dragStartDeltaY - getOffsetTop(listContainerNode));
}

/**
 * Given a list of items, the id of an item being dragged, and the index it is being dragged to, generate a
 * list of item ids reflecting the new post-drag order
 */
function getNewOrderedIds<T>({
  itemDatas,
  itemId,
  newDragIndex,
}: {
  itemDatas: Array<ItemData<T>>;
  itemId: string;
  newDragIndex: number;
}): Array<string> {
  const oldIndex = itemDatas.findIndex((item) => item.id === itemId);
  const newOrderedIds = itemDatas.map(({id}) => id);
  newOrderedIds.splice(oldIndex, 1);
  newOrderedIds.splice(newDragIndex, 0, itemId);
  return newOrderedIds;
}

/**
 * Calculates new dragState as an item is dragged up and down the page
 * @param dragParams Params cached at the beginning of the drag
 * @param dragState Current dragState
 * @param pageY The current y-coordinate of the pointer
 * @package
 */
export function calculateDragMoveState({
  dragParams,
  dragState,
  pageY,
}: {
  dragParams: State['dragParams'];
  dragState: State['dragState'];
  pageY: number;
}): Exclude<State['dragState'], null> {
  assert(dragParams != null, 'Expected non-null dragParams while dragging');
  assert(dragState != null, 'Expected non-null dragState while dragging');
  const {computeTranslateFromMouseY, itemTranslateYs} = dragParams;
  const newTranslateY = computeTranslateFromMouseY(pageY);
  // Find the item that has y translation closest to the y translation of the dragged item.
  const newDragIndex = itemTranslateYs.findIndex((curItemTranslateY: number, index: number) => {
    if (index === itemTranslateYs.length - 1) {
      return true;
    }
    const nextItemTranslateY = itemTranslateYs[index + 1];
    return newTranslateY - curItemTranslateY < nextItemTranslateY - newTranslateY;
  });
  assert(newDragIndex !== -1, 'expected newDragIndex to be -1');
  return {
    translateY: newTranslateY,
    dragIndex: newDragIndex,
  };
}

/**
 * Handles drag-and-drop for a vertically reorderable list of items. Renders them inside <div>'s one after
 * another and allows them to be reordered by dragging and dropping. Autoscrolls the nearest scrollable
 * ancestor when dragging near its edges.
 *
 * Assumptions:
 * - Items are the same height
 * - Caller won't reorder the items
 * - Caller won't rerender the items with a new height while dragging
 * - User uses mouse, since it doesn't handle touch events (ex: on tablet)
 *
 */
class ReorderableList<T extends {} = {}> extends React.Component<ReorderableListProps<T>, State> {
  override state: State = {
    // Information that is constant for the lifetime of the drag operation, like the ID of the item being
    // dragged. This is set on drag start and cleared on drag end, and nullness determines whether or not a
    // drag is happening.
    dragParams: null,
    // Information that changes as the item is being dragged, such as its position. This is updated on
    // every mousemove while dragging.
    dragState: null,
  };

  listContainerRef = React.createRef<HTMLDivElement>();
  private autoscrollIntervalId: number | undefined;
  private scrollContainer: Element | null = null;
  private lastClientY: number = 0;

  override componentWillUnmount(): void {
    this.detachDragListeners();
    this.stopAutoscroll();
  }

  private getItemNode = (index: number): Element =>
    ReactDOM.findDOMNode(this.refs[getItemRef(index)]) as Element;

  private getItemTranslateY = (index: number): number =>
    getOffsetTop(this.getItemNode(index)) - getOffsetTop(this.listContainerRef.current);

  private handleDragStart = (e: ElementMouseEvent, index: number): void => {
    if (
      !assertAndContinue(
        this.state.dragParams == null,
        'Started dragging on item that already started dragging'
      )
    ) {
      return;
    }
    if (this.props.itemDatas.length <= 1) {
      return;
    }
    const itemNode = this.getItemNode(index);
    // Store how far down from its top the dragged item was clicked so it follows the mouse as the mouse
    // moves without jumps.
    const dragStartDeltaY = e.pageY - getOffsetTop(itemNode);
    const maxTranslateY = this.getItemTranslateY(this.props.itemDatas.length - 1);
    const computeTranslateFromMouseY = makeComputeTranslateFromMouseY({
      dragStartDeltaY,
      listContainerNode: this.listContainerRef.current!,
      maxTranslateY,
    });
    this.attachDragListeners();
    this.lastClientY = e.clientY;
    this.scrollContainer = findScrollContainer(this.listContainerRef.current!);
    this.startAutoscroll();
    this.setState({
      dragParams: {
        itemId: this.props.itemDatas[index].id,
        itemHeight: itemNode.clientHeight,
        itemWidth: itemNode.clientWidth,
        computeTranslateFromMouseY,
        // Cache y translations of all items so `handleDragMove` can determine the new index of the dragged
        // item from where its new y translation falls in the list.
        itemTranslateYs: _.range(this.props.itemDatas.length).map((itemIndex) =>
          this.getItemTranslateY(itemIndex)
        ),
      },
      dragState: {
        translateY: computeTranslateFromMouseY(e.pageY),
        dragIndex: index,
      },
    });
    e.preventDefault();
  };

  private handleDragMove = (e: MouseEvent): void => {
    this.lastClientY = e.clientY;
    if (this.state.dragParams == null) {
      return;
    }
    const newDragState = calculateDragMoveState({
      dragParams: this.state.dragParams,
      dragState: this.state.dragState,
      pageY: e.pageY,
    });
    if (newDragState.dragIndex !== this.state.dragState!.dragIndex) {
      this.props.onMoveItem(
        getNewOrderedIds({
          itemDatas: this.props.itemDatas,
          itemId: this.state.dragParams.itemId,
          newDragIndex: newDragState.dragIndex,
        })
      );
    }
    this.setState({
      dragState: newDragState,
    });
  };

  private handleDragEnd = (e: MouseEvent): void => {
    if (!assertAndContinue(this.state.dragParams != null, 'Expected non-null dragParams while dragging')) {
      return;
    }
    if (this.props.onMoveItemEnd) {
      this.props.onMoveItemEnd(this.state.dragParams!.itemId);
    }
    this.setState({
      dragParams: null,
      dragState: null,
    });
    this.stopAutoscroll();
    this.detachDragListeners();
  };

  private attachDragListeners = (): void => {
    // We need to listen to events on the whole window object since the mouse might move outside the list
    // area for the drag operation and we're still interested in the mouse y value.
    window.addEventListener('mousemove', this.handleDragMove);
    window.addEventListener('mouseup', this.handleDragEnd);
  };

  private detachDragListeners = (): void => {
    window.removeEventListener('mousemove', this.handleDragMove);
    window.removeEventListener('mouseup', this.handleDragEnd);
  };

  private getScrollContainerRect = (): {top: number; bottom: number} => {
    if (this.scrollContainer === document.body || this.scrollContainer === document.documentElement) {
      return {top: 0, bottom: window.innerHeight};
    }
    return this.scrollContainer!.getBoundingClientRect();
  };

  private getScrollTop = (): number => {
    if (this.scrollContainer === document.body || this.scrollContainer === document.documentElement) {
      return window.scrollY;
    }
    return this.scrollContainer!.scrollTop;
  };

  private startAutoscroll = (): void => {
    this.autoscrollIntervalId = window.setInterval(() => {
      if (!this.scrollContainer || !this.state.dragParams) {
        return;
      }

      const scrollRect = this.getScrollContainerRect();
      const clientY = this.lastClientY;

      if (clientY > scrollRect.bottom - AUTOSCROLL_TRIGGER_DISTANCE) {
        const scrollBefore = this.getScrollTop();
        if (this.scrollContainer === document.body || this.scrollContainer === document.documentElement) {
          window.scrollBy({top: AUTOSCROLL_DISTANCE, behavior: 'instant'});
        } else {
          this.scrollContainer.scrollBy({top: AUTOSCROLL_DISTANCE, behavior: 'instant'});
        }
        if (this.getScrollTop() !== scrollBefore) {
          this.handleAutoscrollMove();
        }
      } else if (clientY < scrollRect.top + AUTOSCROLL_TRIGGER_DISTANCE) {
        const scrollBefore = this.getScrollTop();
        if (this.scrollContainer === document.body || this.scrollContainer === document.documentElement) {
          window.scrollBy({top: -AUTOSCROLL_DISTANCE, behavior: 'instant'});
        } else {
          this.scrollContainer.scrollBy({top: -AUTOSCROLL_DISTANCE, behavior: 'instant'});
        }
        if (this.getScrollTop() !== scrollBefore) {
          this.handleAutoscrollMove();
        }
      }
    }, AUTOSCROLL_TRIGGER_RATE);
  };

  private stopAutoscroll = (): void => {
    if (this.autoscrollIntervalId != null) {
      clearInterval(this.autoscrollIntervalId);
      this.autoscrollIntervalId = undefined;
    }
    this.scrollContainer = null;
  };

  private handleAutoscrollMove = (): void => {
    if (this.state.dragParams == null || this.state.dragState == null) {
      return;
    }
    const newDragState = calculateDragMoveState({
      dragParams: this.state.dragParams,
      dragState: this.state.dragState,
      pageY: this.lastClientY + window.scrollY,
    });
    if (newDragState.dragIndex !== this.state.dragState.dragIndex) {
      this.props.onMoveItem(
        getNewOrderedIds({
          itemDatas: this.props.itemDatas,
          itemId: this.state.dragParams.itemId,
          newDragIndex: newDragState.dragIndex,
        })
      );
    }
    this.setState({dragState: newDragState});
  };

  override render(): JSX.Element {
    let draggedItemId: string | null = null;
    if (this.state.dragParams != null) {
      draggedItemId = this.state.dragParams.itemId;
    }

    const renderedItems = this.props.itemDatas.map((itemData, index) => (
      <ReorderableListItem
        key={itemData.id}
        ref={getItemRef(index)}
        itemClass={itemData.itemClass}
        itemProps={itemData.itemProps}
        onDragStart={this.handleDragStart}
        index={index}
        isInvisible={Boolean(draggedItemId) && draggedItemId === itemData.id}
      />
    ));

    if (draggedItemId != null) {
      const draggedItemData = _.findWhere(this.props.itemDatas, {id: draggedItemId});
      if (draggedItemData != null && this.state.dragState != null && this.state.dragParams != null) {
        renderedItems.unshift(
          <ReorderableListItem
            key="draggedItem"
            itemClass={draggedItemData.itemClass}
            itemProps={draggedItemData.itemProps}
            dragLayoutParams={{
              translateY: this.state.dragState.translateY,
              height: this.state.dragParams.itemHeight,
              width: this.state.dragParams.itemWidth,
            }}
          />
        );
      }
    }

    return (
      <div
        className="u-position-relative"
        ref={this.listContainerRef}
        {...getTestProps({component: 'ReorderableList', element: 'root'})}
      >
        {renderedItems}
      </div>
    );
  }
}

export default ReorderableList;
